import React from 'react';
import { Target, Eye, Shield, CheckCircle, Lightbulb, Users, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutUs: React.FC = () => {
  const { t } = useTranslation();

  const companies = [
    { name: 'AMIVIK Farmácias', logo: '/assets/logos/amivik-farmacia.png' },
    { name: 'FIA MENDES', logo: '/assets/logos/fiaMendes.png' },
    { name: 'Gráfica fiaMendes', logo: '/assets/logos/grafica-fia-mendes.png' },
    { name: 'artesMais', logo: '/assets/logos/artesmais.png' }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FF6B35] via-[#4A90E2] to-[#2C5282] text-white py-20 px-6">
        <div className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} 
        />
        <div className="container mx-auto max-w-4xl relative z-10 text-center animate-fadeIn">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('aboutUs.title')}</h1>
          <p className="text-xl md:text-2xl font-medium leading-relaxed text-white mb-6">
            {t('aboutUs.hero1')}
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-8 text-left md:text-center">
            {t('aboutUs.hero2')}
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 text-left mb-12 border border-white/20">
            <p className="text-lg font-semibold text-white mb-6">{t('aboutUs.hero3')}</p>
            <ul className="space-y-4">
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-orange-400 mr-3 shrink-0" />
                <p className="text-white/90"><strong className="text-white">{t('aboutUs.model1Title')}</strong> {t('aboutUs.model1Text')}</p>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-orange-400 mr-3 shrink-0" />
                <p className="text-white/90"><strong className="text-white">{t('aboutUs.model2Title')}</strong> {t('aboutUs.model2Text')}</p>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-orange-400 mr-3 shrink-0" />
                <p className="text-white/90"><strong className="text-white">{t('aboutUs.model3Title')}</strong> {t('aboutUs.model3Text')}</p>
              </li>
            </ul>
          </div>

          <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-12 text-left md:text-center">
            {t('aboutUs.hero4')}
          </p>

          {/* Route Strip */}
          <div className="route" role="img" aria-label="Trajecto de entrega: recolha, em trânsito, entregue">
            <div className="route-track">
              <div className="route-pulse"></div>
            </div>
            <div className="route-points">
              <span className="route-point">{t('aboutUs.routePoint1')}</span>
              <span className="route-point">{t('aboutUs.routePoint2')}</span>
              <span className="route-point">{t('aboutUs.routePoint3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Missão e Visão */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl hover:border-orange-200 transition-all transform hover:-translate-y-1">
            <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-6 text-[#FF6B35]">
              <Target size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('aboutUs.mission')}</h2>
            <h3 className="text-lg font-semibold text-[#FF6B35] mb-4">{t('aboutUs.missionSubtitle')}</h3>
            <p className="text-gray-600 leading-relaxed">
              {t('aboutUs.missionText')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl hover:border-blue-200 transition-all transform hover:-translate-y-1">
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 text-[#4A90E2]">
              <Eye size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('aboutUs.vision')}</h2>
            <h3 className="text-lg font-semibold text-[#4A90E2] mb-4">{t('aboutUs.visionSubtitle')}</h3>
            <p className="text-gray-600 leading-relaxed">
              {t('aboutUs.visionText')}
            </p>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('aboutUs.valuesTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('aboutUs.valuesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 text-red-500">
                <Shield size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutUs.val1Title')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('aboutUs.val1Text')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 text-green-500">
                <CheckCircle size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutUs.val2Title')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('aboutUs.val2Text')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 text-purple-500">
                <Lightbulb size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutUs.val3Title')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('aboutUs.val3Text')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-500">
                <Users size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('aboutUs.val4Title')}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('aboutUs.val4Text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quem Confia em Nós / Partners Section */}
      <section className="bg-white py-16 px-6 border-t border-gray-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-[#FF6B35] text-xs font-bold uppercase tracking-wider mb-4">
              <Building2 size={14} />
              {t('aboutUs.partnersTitle')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('aboutUs.partnersTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
              {t('aboutUs.partnersSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center justify-center max-w-4xl mx-auto">
            {companies.map((c, idx) => (
              <div 
                key={idx} 
                className="flex justify-center items-center py-5 px-6 bg-slate-50 hover:bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition duration-300 h-24"
              >
                <img 
                  src={process.env.PUBLIC_URL + c.logo} 
                  alt={c.name} 
                  className="max-h-12 max-w-full object-contain grayscale hover:grayscale-0 transition duration-300" 
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
